import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNutritionSummaryData } from '../hooks/nutritionSummaryData';
import {
  VictoryAxis,
  VictoryChart,
  VictoryScatter,
  VictoryLine,
} from 'victory';
import axios from 'axios';
import Api from '../Api/Api.json';
import '../styles/VisualizeWellnessData.css';

const VisualizeNutritionData = () => {
    const navigate = useNavigate();
    
    // Nutrition data visualization state
    const [selectedTimeSpan, setSelectedTimeSpan] = useState('week');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [nutritionData, setNutritionData] = useState([]);
    const [dataType, setDataType] = useState('total_calories');
    const [patientId, setPatientId] = useState(null);
    
    // Meal history state
    const [mealList, setMealList] = useState([]);
    const [selectedMeal, setSelectedMeal] = useState(null);
    const [mealDetails, setMealDetails] = useState(null);
    const [isLoadingList, setIsLoadingList] = useState(false);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [error, setError] = useState(null);
    const [userId, setUserId] = useState(null);
    
    // Get patient ID from localStorage
    useEffect(() => {
        if (localStorage.getItem('user_info') !== null) {
            const userInfo = JSON.parse(localStorage.getItem('user_info'));
            const id = userInfo.di_id;
            setPatientId(id);
            setUserId(id);
        }
    }, []);
    
    // Call the nutrition summary API
    const { data: nutritionSummaryData, isLoading, error: nutritionError } = useNutritionSummaryData(patientId);
    
    // Fetch meal list
    useEffect(() => {
        if (userId) {
            fetchMealList(userId);
        }
    }, [userId]);
    
    const fetchMealList = async (id) => {
        setIsLoadingList(true);
        setError(null);
        
        try {
            const headers = { headers: { "Content-Type": "application/json" } };
            const url = Api.FITNESS_API;
            const requestData = {
                task: "getFoodCaloriesList",
                user_id: id
            };

            const response = await axios.post(url, requestData, headers);
            
            // Handle different response structures
            let responseData = response.data;
            if (responseData?.body) responseData = responseData.body;
            if (responseData?.data) responseData = responseData.data;

            if (Array.isArray(responseData)) {
                // Sort by created_at descending (newest first)
                const sortedList = responseData.sort((a, b) => {
                    const dateA = new Date(a.created_at || a.crated_at || 0);
                    const dateB = new Date(b.created_at || b.crated_at || 0);
                    return dateB - dateA;
                });
                setMealList(sortedList);
            } else {
                setMealList([]);
            }
        } catch (error) {
            console.error('Error fetching meal list:', error);
            setError('Failed to load meal history. Please try again.');
            setMealList([]);
        } finally {
            setIsLoadingList(false);
        }
    };
    
    const fetchMealDetails = async (createdAt) => {
        if (!userId) return;
        
        setIsLoadingDetails(true);
        setError(null);
        
        try {
            const headers = { headers: { "Content-Type": "application/json" } };
            const url = Api.FITNESS_API;
            const requestData = {
                task: "getUserMealCaloriesAtTimestamp",
                user_id: userId,
                created_at: createdAt
            };

            const response = await axios.post(url, requestData, headers);
            
            // Handle different response structures
            let responseData = response.data;
            if (responseData?.body) responseData = responseData.body;
            if (responseData?.data) responseData = responseData.data;

            if (responseData && responseData.status === "success" && responseData.results && Array.isArray(responseData.results) && responseData.results.length > 0) {
                setMealDetails(responseData);
                setError(null);
            } else if (responseData && responseData.status === "failure") {
                setError('No food items found for this meal.');
                setMealDetails(responseData);
            } else {
                setError('No meal details found for this timestamp.');
                setMealDetails(null);
            }
        } catch (error) {
            console.error('Error fetching meal details:', error);
            setError('Failed to load meal details. Please try again.');
            setMealDetails(null);
        } finally {
            setIsLoadingDetails(false);
        }
    };
    
    const handleMealClick = (meal) => {
        const createdAt = meal.created_at || meal.crated_at;
        if (createdAt) {
            setSelectedMeal(meal);
            fetchMealDetails(createdAt);
        }
    };
    
    const formatDateTime = (dateString) => {
        if (!dateString) return 'Unknown';
        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };
    
    // Handle data type changes
    const handleDataTypeChange = (newDataType) => {
        setDataType(newDataType);
    };
    
    // Function to get unit for current nutrition type
    const getNutritionUnit = () => {
        const unitMap = {
            'calories': 'kcal',
            'total_calories': 'kcal',
            'protein': 'g',
            'carbohydrate': 'g',
            'fat': 'g',
            'vegetable': 'g',
            'vegetables': 'g',
            'vitamin_a': 'µg',
            'vitamin_c': 'mg',
            'vitamin_d': 'µg',
            'vitamin_b12': 'µg',
            'iron': 'mg',
            'magnesium': 'mg',
            'potassium': 'mg',
            'calcium': 'mg',
        };
        return unitMap[dataType] || '';
    };
    
    // Function to get ideal amount for current nutrition type
    const getIdealAmount = () => {
        const idealMap = {
            'calories': 2000,
            'total_calories': 2000,
            'protein': 80,
            'carbohydrate': 250,
            'fat': 65,
            'vegetable': 400,
            'vegetables': 400,
            'vitamin_a': 900,
            'vitamin_c': 90,
            'vitamin_d': 20,
            'vitamin_b12': 2.4,
            'iron': 18,
            'magnesium': 420,
            'potassium': 3500,
            'calcium': 1000,
        };
        return idealMap[dataType] ?? 100;
    };
    
    // Auto-set current date to match available data when switching time spans or data types
    useEffect(() => {
        if (nutritionSummaryData && nutritionSummaryData[dataType] && nutritionSummaryData[dataType][selectedTimeSpan]) {
            const timeSpanData = nutritionSummaryData[dataType][selectedTimeSpan];
            if (timeSpanData.length > 0) {
                // Get the first available date from the data
                const firstDataKey = Object.keys(timeSpanData[0])[0];
                let availableDate;
                
                // For weekly/monthly/yearly data, the key format is "YYYY-MM-DD"
                const [year, month, day] = firstDataKey.split('-').map(Number);
                availableDate = new Date(year, month - 1, day); // month is 0-indexed
                
                // Only auto-set if dates are different
                if (availableDate.toDateString() !== currentDate.toDateString()) {
                    setCurrentDate(availableDate);
                }
            }
        }
    }, [selectedTimeSpan, nutritionSummaryData, dataType]);
    
    // Get nutrition data from API response
    const getNutritionData = useCallback((timeSpan, date) => {
        const data = [];
        
        // If we have API data, use the structured format
        if (nutritionSummaryData && nutritionSummaryData[dataType]) {
            const nutritionTypeData = nutritionSummaryData[dataType];
            const timeSpanData = nutritionTypeData[timeSpan] || [];
            
            // Calculate the date range based on current date and time span
            let startDateStr, endDateStr;
            let expectedDataPoints = [];
            
            switch(timeSpan) {
                case 'week':
                    // Create week date strings
                    const weekStart = new Date(date);
                    weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
                    const weekEnd = new Date(weekStart);
                    weekEnd.setDate(weekStart.getDate() + 6); // End of week (Saturday)
                    
                    startDateStr = weekStart.toISOString().split('T')[0];
                    endDateStr = weekEnd.toISOString().split('T')[0];
                    
                    // Create 7 daily data points as strings
                    for (let i = 0; i < 7; i++) {
                        const dayDate = new Date(weekStart);
                        dayDate.setDate(weekStart.getDate() + i);
                        expectedDataPoints.push(dayDate.toISOString().split('T')[0]);
                    }
                    break;
                case 'month':
                    // Create month date strings
                    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
                    const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                    
                    startDateStr = monthStart.toISOString().split('T')[0];
                    endDateStr = monthEnd.toISOString().split('T')[0];
                    
                    // Create daily data points for the entire month
                    const daysInMonth = monthEnd.getDate();
                    for (let i = 0; i < daysInMonth; i++) {
                        const dayDate = new Date(monthStart);
                        dayDate.setDate(monthStart.getDate() + i);
                        expectedDataPoints.push(dayDate.toISOString().split('T')[0]);
                    }
                    break;
                case 'year':
                    // Create year date strings
                    const yearStart = new Date(date.getFullYear(), 0, 1);
                    const yearEnd = new Date(date.getFullYear(), 11, 31);
                    
                    startDateStr = yearStart.toISOString().split('T')[0];
                    endDateStr = yearEnd.toISOString().split('T')[0];
                    
                    // Create 12 monthly data points as strings
                    for (let i = 0; i < 12; i++) {
                        try {
                            const monthDate = new Date(date.getFullYear(), i, 1);
                            // Validate the date before adding it
                            if (!isNaN(monthDate.getTime())) {
                                expectedDataPoints.push(monthDate.toISOString().split('T')[0]);
                            }
                        } catch (error) {
                            // Silently handle date creation errors
                        }
                    }
                    break;
                default:
                    return [];
            }
            
            // Filter data based on date range using string comparison
            const filteredData = timeSpanData.filter(item => {
                const dateKey = Object.keys(item)[0];
                // For weekly/monthly/yearly data, the key format is "YYYY-MM-DD"
                // Use string comparison for date range
                return dateKey >= startDateStr && dateKey <= endDateStr;
            });
            
            // Create a map of available data by date string
            const dataMap = new Map();
            filteredData.forEach(item => {
                const dateKey = Object.keys(item)[0];
                const dataObj = item[dateKey];
                dataMap.set(dateKey, dataObj);
            });
            
            // Get ideal amount for current nutrition type
            const idealAmount = getIdealAmount();
            
            // Create complete timeline with all expected data points
            expectedDataPoints.forEach((expectedDateStr, index) => {
                const dateKey = expectedDateStr;
                const availableData = dataMap.get(dateKey);
                
                if (availableData) {
                    // Data exists for this date - use 'total' value
                    const actualValue = availableData.total || availableData.avg || null;
                    data.push({
                        x: index + 1,
                        y: actualValue,
                        ideal: idealAmount,
                        hasData: true
                    });
                } else {
                    // No data for this date - create empty data point
                    data.push({
                        x: index + 1,
                        y: null,
                        ideal: idealAmount,
                        hasData: false
                    });
                }
            });
            
            return data;
        }
        
        // Fallback: return empty data if no API data
        return [];
    }, [nutritionSummaryData, dataType]);

    // Navigation functions
    const goBackward = () => {
        const newDate = new Date(currentDate);
        switch(selectedTimeSpan) {
            case 'week':
                newDate.setDate(newDate.getDate() - 7);
                break;
            case 'month':
                newDate.setMonth(newDate.getMonth() - 1);
                break;
            case 'year':
                newDate.setFullYear(newDate.getFullYear() - 1);
                break;
            default:
                break;
        }
        setCurrentDate(newDate);
    };

    const goForward = () => {
        const newDate = new Date(currentDate);
        switch(selectedTimeSpan) {
            case 'week':
                newDate.setDate(newDate.getDate() + 7);
                break;
            case 'month':
                newDate.setMonth(newDate.getMonth() + 1);
                break;
            case 'year':
                newDate.setFullYear(newDate.getFullYear() + 1);
                break;
            default:
                break;
        }
        setCurrentDate(newDate);
    };

    // Update nutrition data when time span, date, or data type changes
    useEffect(() => {
        const data = getNutritionData(selectedTimeSpan, currentDate);
        setNutritionData(data);
    }, [selectedTimeSpan, currentDate, getNutritionData]);

    // Format date for display
    const formatDisplayDate = () => {
        switch(selectedTimeSpan) {
            case 'week':
                const weekStart = new Date(currentDate);
                weekStart.setDate(currentDate.getDate() - currentDate.getDay());
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekStart.getDate() + 6);
                return `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`;
            case 'month':
                return currentDate.toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long' 
                });
            case 'year':
                return currentDate.getFullYear().toString();
            default:
                return '';
        }
    };
    
    // Get color for data type
    const getDataTypeColor = () => {
        const colorMap = {
            'calories': '#ef4444',
            'total_calories': '#ef4444',
            'protein': '#3b82f6',
            'carbohydrate': '#10b981',
            'fat': '#f59e0b',
            'vegetable': '#8b5cf6',
            'vegetables': '#8b5cf6',
            'vitamin_a': '#f97316',
            'vitamin_c': '#22c55e',
            'vitamin_d': '#eab308',
            'vitamin_b12': '#a855f7',
            'iron': '#64748b',
            'magnesium': '#06b6d4',
            'potassium': '#ec4899',
            'calcium': '#94a3b8',
        };
        return colorMap[dataType] || '#ef4444';
    };

    return (
        <div className="min-h-screen pb-32 safe-area-top">
            {/* Black background */}
            <div className="absolute inset-0 bg-black"></div>
            
            <div className="container mx-auto py-6 relative z-10" style={{ paddingLeft: '5px', paddingRight: '5px', paddingBottom: '80px' }}>
        
        {/* Back Button */}
        <div className="mb-4">
            <button
                onClick={() => navigate(-1)}
                className="flex items-center text-white hover:text-blue-400 transition-colors duration-200"
            >
                <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                    />
                </svg>
                <span className="text-sm font-medium text-white">Go Back</span>
            </button>
        </div>
        
        {/* Loading State */}
        {isLoading && (
            <div className="mb-6">
                <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-white">Loading nutrition data...</span>
                </div>
            </div>
        )}
        
        {/* Error State */}
        {nutritionError && (
            <div className="bg-[#161B22] border border-[#30363D] rounded-lg p-4 mb-6">
                <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-red-400">Error loading nutrition data: {nutritionError?.message ?? String(nutritionError)}</span>
                </div>
            </div>
        )}
        
        {/* Nutrition Data Content - Card removed, content directly on page */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">
                    Nutrition
                </h3>
                <div className="text-sm text-white">{formatDisplayDate()}</div>
            </div>
            
            {/* Data Type Selector */}
            <div className="mb-4">
                <label className="block text-sm font-medium text-white mb-2">Select Nutrition Type:</label>
                <select
                    value={dataType}
                    onChange={(e) => handleDataTypeChange(e.target.value)}
                    className="w-full px-3 py-2 border border-[#30363D] bg-[#161B22] text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    style={{ 
                        colorScheme: 'dark',
                        backgroundColor: '#161B22',
                        color: '#F0F6FC',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        appearance: 'none'
                    }}
                >
                    <option value="total_calories" style={{ backgroundColor: '#161B22', color: '#F0F6FC' }}>Total Calories</option>
                    <option value="protein" style={{ backgroundColor: '#161B22', color: '#F0F6FC' }}>Protein</option>
                    <option value="carbohydrate" style={{ backgroundColor: '#161B22', color: '#F0F6FC' }}>Carbohydrate</option>
                    <option value="fat" style={{ backgroundColor: '#161B22', color: '#F0F6FC' }}>Fat</option>
                    <option value="vegetables" style={{ backgroundColor: '#161B22', color: '#F0F6FC' }}>Vegetables</option>
                    <option value="vitamin_a" style={{ backgroundColor: '#161B22', color: '#F0F6FC' }}>Vitamin A</option>
                    <option value="vitamin_c" style={{ backgroundColor: '#161B22', color: '#F0F6FC' }}>Vitamin C</option>
                    <option value="vitamin_d" style={{ backgroundColor: '#161B22', color: '#F0F6FC' }}>Vitamin D</option>
                    <option value="vitamin_b12" style={{ backgroundColor: '#161B22', color: '#F0F6FC' }}>Vitamin B12</option>
                    <option value="iron" style={{ backgroundColor: '#161B22', color: '#F0F6FC' }}>Iron</option>
                    <option value="magnesium" style={{ backgroundColor: '#161B22', color: '#F0F6FC' }}>Magnesium</option>
                    <option value="potassium" style={{ backgroundColor: '#161B22', color: '#F0F6FC' }}>Potassium</option>
                    <option value="calcium" style={{ backgroundColor: '#161B22', color: '#F0F6FC' }}>Calcium</option>
                </select>
            </div>
            
            {/* Show message if selected data type is not available in API */}
            {nutritionSummaryData && !nutritionSummaryData[dataType] && (
                <div className="mb-4 p-3 bg-[#161B22] border border-[#30363D] rounded-md">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="text-yellow-400 text-sm">
                            {dataType} data is not available in the API response.
                        </span>
                    </div>
                </div>
            )}
            
            {/* Time Span Tabs */}
            <div className="flex space-x-1 mb-4 bg-[#21262D] rounded-lg p-1">
                {['week', 'month', 'year'].map((span) => (
                    <button
                        key={span}
                        onClick={() => setSelectedTimeSpan(span)}
                        className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                            selectedTimeSpan === span
                                ? 'bg-[#58A6FF] text-white shadow-sm'
                                : 'text-white hover:text-blue-400'
                        }`}
                    >
                        {span.charAt(0).toUpperCase() + span.slice(1)}
                    </button>
                ))}
            </div>
            
            {/* Navigation Controls */}
            <div className="flex items-center justify-between mb-4">
                <button
                    onClick={goBackward}
                    className="p-2 text-white hover:text-blue-400 hover:bg-[#21262D] rounded-md transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <div className="text-sm text-white font-medium">
                    {formatDisplayDate()}
                </div>
                <button
                    onClick={goForward}
                    className="p-2 text-white hover:text-blue-400 hover:bg-[#21262D] rounded-md transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
            
            {/* Chart Legend and Data Summary */}
            <div className="mb-2 text-xs text-white flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full mr-1" style={{
                            backgroundColor: getDataTypeColor()
                        }}></div>
                        <span className="text-xs">Data Available</span>
                    </div>
                    <div className="flex items-center">
                        <div className="w-2 h-2 transform rotate-45 mr-1" style={{
                            backgroundColor: "#d1d5db",
                            border: "1px solid #9ca3af"
                        }}></div>
                        <span className="text-xs">No Data</span>
                    </div>
                    {getNutritionUnit() && (
                        <div className="flex items-center">
                            <div className="w-2 h-2 rounded-sm mr-1" style={{
                                backgroundColor: "#9ca3af",
                                border: "1px solid #6b7280"
                            }}></div>
                            <span className="text-xs">unit: {getNutritionUnit()}</span>
                        </div>
                    )}
                </div>
                {nutritionData.length > 0 && (
                    <div className="text-right">
                        <span className="font-medium">
                            {nutritionData.filter(d => d.hasData).length} of {nutritionData.length} data points available
                        </span>
                    </div>
                )}
            </div>
            
            {/* Nutrition Data Chart */}
            <div className="h-64">
                {nutritionData.length > 0 ? (
                <VictoryChart
                    domain={{ 
                        y: (() => {
                            const idealAmount = getIdealAmount();
                            const actualValues = nutritionData.filter(d => d.hasData && d.y !== null).map(d => d.y);
                            const maxActual = actualValues.length > 0 ? Math.max(...actualValues) : 0;
                            // Use the maximum of actual values and ideal amount, then add 20% padding
                            const maxValue = Math.max(maxActual, idealAmount);
                            return [0, maxValue * 1.2];
                        })(),
                        x: nutritionData.length > 0 ? [0, nutritionData.length + 1] :
                            selectedTimeSpan === 'week' ? [0, 8] :
                            selectedTimeSpan === 'month' ? 
                            (() => {
                                const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
                                return [0, daysInMonth + 1];
                            })() : [0, 13]
                    }}
                    height={250}
                    padding={{ top: 20, bottom: 50, left: 60, right: 5 }}
                    style={{
                        background: { fill: 'transparent' }
                    }}
                >
                    <VictoryAxis
                        tickValues={nutritionData.length > 0 ? 
                            (() => {
                                switch(selectedTimeSpan) {
                                    case 'week':
                                        return [1, 2, 3, 4, 5, 6, 7];
                                    case 'month':
                                        const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
                                        const monthTicks = [];
                                        for (let i = 1; i <= daysInMonth; i += 3) {
                                            monthTicks.push(i);
                                        }
                                        return monthTicks;
                                    case 'year':
                                        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
                                    default:
                                        return nutritionData.map((_, index) => index + 1);
                                }
                            })() :
                            selectedTimeSpan === 'week' ? 
                            [1, 2, 3, 4, 5, 6, 7] :
                            selectedTimeSpan === 'month' ? 
                            (() => {
                                const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
                                const monthTicks = [];
                                for (let i = 1; i <= daysInMonth; i += 3) {
                                    monthTicks.push(i);
                                }
                                return monthTicks;
                            })() :
                            [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
                        }
                        tickFormat={nutritionData.length > 0 ? 
                            (() => {
                                switch(selectedTimeSpan) {
                                    case 'week':
                                        return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                                    case 'month':
                                        const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
                                        const monthLabels = [];
                                        for (let i = 1; i <= daysInMonth; i += 3) {
                                            monthLabels.push(i.toString());
                                        }
                                        return monthLabels;
                                    case 'year':
                                        return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                    default:
                                        return [];
                                }
                            })() :
                            selectedTimeSpan === 'week' ? 
                            ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] :
                            selectedTimeSpan === 'month' ? 
                            (() => {
                                const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
                                const monthLabels = [];
                                for (let i = 1; i <= daysInMonth; i += 3) {
                                    monthLabels.push(i.toString());
                                }
                                return monthLabels;
                            })() :
                            ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
                        }
                        style={{
                            axis: { stroke: '#8B949E' },
                            tickLabels: { fill: '#F0F6FC', fontSize: 21, padding: 5, fontWeight: 500 },
                            grid: null,
                        }}
                    />
                    
                    <VictoryAxis
                        dependentAxis
                        tickFormat={(t) => {
                            if (t >= 1000) {
                                const kValue = t / 1000;
                                return kValue % 1 === 0 ? `${kValue}K` : `${kValue.toFixed(1)}K`;
                            } else if (t >= 100) {
                                return `${(t / 1000).toFixed(1)}K`;
                            } else {
                                return t.toString();
                            }
                        }}
                        style={{
                            axis: { stroke: '#8B949E' },
                            tickLabels: { fill: '#F0F6FC', fontSize: 27, padding: 5, fontWeight: 500 },
                            grid: { stroke: '#8B949E', strokeWidth: 0.5, strokeDasharray: 'none' },
                        }}
                    />
                    
                    {/* Ideal amount reference line */}
                    <VictoryLine
                        data={nutritionData.map(d => ({ x: d.x, y: d.ideal || getIdealAmount() }))}
                        style={{
                            data: {
                                stroke: '#9ca3af',
                                strokeWidth: 1.5,
                                strokeDasharray: '5,5'
                            }
                        }}
                    />
                    
                    {/* Arrows from actual to ideal amounts */}
                    {nutritionData.filter(d => d.hasData && d.y !== null && d.ideal !== undefined).map((datum, index) => {
                        const actualY = datum.y;
                        const idealY = datum.ideal;
                        if (Math.abs(actualY - idealY) < 1) return null; // No arrow if values are very close
                        
                        // Draw arrow line from actual to ideal
                        return (
                            <VictoryLine
                                key={`arrow-${index}`}
                                data={[
                                    { x: datum.x, y: actualY },
                                    { x: datum.x, y: idealY }
                                ]}
                                style={{
                                    data: {
                                        stroke: actualY > idealY ? '#ef4444' : '#10b981', // Red if above ideal, green if below
                                        strokeWidth: 2,
                                        strokeDasharray: '4,4'
                                    }
                                }}
                            />
                        );
                    })}
                    
                    {/* Ideal amount markers (arrowheads) - separate for above and below */}
                    <VictoryScatter
                        data={nutritionData.filter(d => d.hasData && d.y !== null && d.ideal !== undefined && d.y > d.ideal).map(d => ({
                            x: d.x,
                            y: d.ideal,
                            actual: d.y
                        }))}
                        x="x"
                        y="y"
                        size={6}
                        style={{
                            data: {
                                fill: '#ef4444',
                                stroke: '#fff',
                                strokeWidth: 1.5
                            }
                        }}
                        symbol="triangleDown"
                    />
                    <VictoryScatter
                        data={nutritionData.filter(d => d.hasData && d.y !== null && d.ideal !== undefined && d.y < d.ideal).map(d => ({
                            x: d.x,
                            y: d.ideal,
                            actual: d.y
                        }))}
                        x="x"
                        y="y"
                        size={6}
                        style={{
                            data: {
                                fill: '#10b981',
                                stroke: '#fff',
                                strokeWidth: 1.5
                            }
                        }}
                        symbol="triangleUp"
                    />
                    
                    {/* Line Chart */}
                    <VictoryLine
                        data={nutritionData.filter(d => d.hasData && d.y !== null)}
                        style={{
                            data: { stroke: "#58A6FF", strokeWidth: 2 }
                        }}
                    />
                    
                    {/* Data points for days with data */}
                    <VictoryScatter
                        data={nutritionData.filter(d => d.hasData && d.y !== null)}
                        x="x"
                        y="y"
                        size={4}
                        style={{
                            data: { 
                                fill: "#FFFFFF",
                                stroke: "#58A6FF",
                                strokeWidth: 2
                            }
                        }}
                        labelComponent={<div />}
                    />
                    
                    {/* Indicators for missing data points */}
                    <VictoryScatter
                        data={nutritionData.filter(d => !d.hasData)}
                        x="x"
                        y={(d) => {
                            // Position at the middle of the Y domain
                            const maxY = nutritionData.filter(d => d.hasData && d.y !== null).length > 0 
                                ? Math.max(...nutritionData.filter(d => d.hasData && d.y !== null).map(d => d.y)) 
                                : 100;
                            return maxY / 2;
                        }}
                        size={2}
                        style={{
                            data: { 
                                fill: "#d1d5db",
                                stroke: "#9ca3af",
                                strokeWidth: 1
                            }
                        }}
                        symbol="diamond"
                        labelComponent={<div />}
                    />
                </VictoryChart>
                ) : (
                    <div className="flex items-center justify-center h-full text-white">
                        <div className="text-center">
                            <svg className="w-12 h-12 mx-auto mb-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            <p className="text-white">No data available for {dataType}</p>
                        </div>
                    </div>
                )}
            </div>
        
        {/* Error Message for Meal History */}
        {error && (
          <div className="mb-6 bg-[#161B22] border border-[#30363D] rounded-lg p-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-400">{error}</span>
            </div>
          </div>
        )}
        
        {/* Meal Records and Meal Details Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Meal List */}
          <div className="bg-[#161B22] rounded-lg shadow-md overflow-hidden border border-[#30363D]">
            <div className="bg-[#58A6FF] p-4 text-white">
              <h2 className="text-xl font-semibold">Meal Records</h2>
            </div>
            <div className="p-4">
              {isLoadingList ? (
                <div className="flex items-center justify-center py-8">
                  <svg className="animate-spin h-8 w-8 text-[#58A6FF]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : mealList.length === 0 ? (
                <div className="text-center py-8 text-white">
                  <p>No meal records found</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {mealList.map((meal, index) => {
                    const createdAt = meal.created_at || meal.crated_at;
                    const isSelected = selectedMeal && (selectedMeal.created_at === createdAt || selectedMeal.crated_at === createdAt);
                    
                    return (
                      <button
                        key={index}
                        onClick={() => handleMealClick(meal)}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                          isSelected
                            ? 'border-[#58A6FF] bg-[#21262D]'
                            : 'border-[#30363D] hover:border-[#58A6FF] hover:bg-[#21262D]'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-white">
                              Meal #{mealList.length - index}
                            </div>
                            <div className="text-sm text-white mt-1">
                              {formatDateTime(createdAt)}
                            </div>
                          </div>
                          <svg
                            className={`w-5 h-5 transition-transform ${
                              isSelected ? 'text-[#58A6FF]' : 'text-white'
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Meal Details */}
          <div className="bg-[#161B22] rounded-lg shadow-md overflow-hidden border border-[#30363D]">
            <div className="bg-[#58A6FF] p-4 text-white">
              <h2 className="text-xl font-semibold">Meal Details</h2>
            </div>
            <div className="p-4 pb-24">
              {isLoadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <svg className="animate-spin h-8 w-8 text-[#58A6FF]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : !selectedMeal ? (
                <div className="text-center py-8 text-white">
                  <p>Select a meal from the list to view details</p>
                </div>
              ) : mealDetails ? (
                <div className="space-y-4">
                  {/* Meal Image if available */}
                  {mealDetails.photo_str && (
                    <div className="flex justify-center">
                      <img
                        src={`data:image/jpeg;base64,${mealDetails.photo_str}`}
                        alt="Meal"
                        className="max-w-full h-auto rounded-lg shadow-md"
                      />
                    </div>
                  )}

                  {/* Food Items Table */}
                  {mealDetails.results && Array.isArray(mealDetails.results) && mealDetails.results.length > 0 ? (
                    <div className="flex flex-col">
                      <h3 className="text-lg font-semibold text-white mb-3">Detected Food Items</h3>
                      <div className="border border-[#30363D] rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <div className="overflow-y-auto max-h-[500px]">
                            <table className="min-w-full bg-[#21262D]">
                              <thead className="bg-[#58A6FF] text-white">
                                <tr>
                                  <th className="px-4 py-3 text-left text-sm font-semibold">Item</th>
                                  <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                                  <th className="px-4 py-3 text-left text-sm font-semibold">Calories</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-[#30363D]">
                                {mealDetails.results.map((item, index) => {
                                  // Extract item name, amount, and calories from the result object
                                  const itemKey = `item_${index + 1}`;
                                  let itemName = item[itemKey] || item.item || item[`item_${index}`] || 'Unknown';
                                  
                                  // If still not found, try to find any key starting with "item_"
                                  if (itemName === 'Unknown') {
                                    const itemKeys = Object.keys(item).filter(key => key.startsWith('item_'));
                                    if (itemKeys.length > 0) {
                                      itemName = item[itemKeys[0]];
                                    }
                                  }
                                  
                                  const amount = item.amount || 'N/A';
                                  const calories = item.calories || 'N/A';
                                  
                                  return (
                                    <tr key={index} className="hover:bg-[#1f2937]">
                                      <td className="px-4 py-3 text-sm text-white font-medium">{itemName}</td>
                                      <td className="px-4 py-3 text-sm text-white">{amount}</td>
                                      <td className="px-4 py-3 text-sm text-white font-semibold">{calories} cal</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot className="bg-[#21262D]">
                                <tr>
                                  <td className="px-4 py-3 text-sm font-bold text-white" colSpan="2">Total Calories</td>
                                  <td className="px-4 py-3 text-sm font-bold text-green-400">
                                    {mealDetails.results.reduce((sum, item) => {
                                      const calories = parseInt(item.calories) || 0;
                                      return sum + calories;
                                    }, 0)} cal
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-white">
                      <p>No food items found for this meal</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-white">
                  <p>No details available for this meal</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
            </div>
        </div>
    );
};

export default VisualizeNutritionData;

